import { Button, Group, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { ApiError } from 'src/error/ApiError';
import useAddSkillLibraryEntry from './hooks/useAddSkillLibraryEntry';
import type { SkillLibraryEntryListItem } from './types';

type AddToFirmButtonProps = {
  entry: SkillLibraryEntryListItem;
  firmName: string | null;
};

const BUTTON_MAX_WIDTH = 230;

export default function AddToFirmButton({ entry, firmName }: AddToFirmButtonProps) {
  const { mutateAsync, isPending } = useAddSkillLibraryEntry();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [justAdded, setJustAdded] = useState(false);
  const addedRef = useRef<HTMLDivElement>(null);

  const displayFirmName = firmName ?? 'your firm';

  // Adding unmounts this button in favour of the "Added" indicator below, which would
  // otherwise drop keyboard focus to the document body. Only steal focus for a transition we
  // just caused ourselves, never for an entry that was already added when the row first mounted.
  useEffect(() => {
    if (justAdded && entry.alreadyAdded) {
      addedRef.current?.focus();
    }
  }, [justAdded, entry.alreadyAdded]);

  const handleAdd = async () => {
    setErrorMessage(null);
    try {
      await mutateAsync(entry.id);
      setJustAdded(true);
      notifications.show({ message: `Added to ${displayFirmName}`, color: 'green' });
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'An error occurred';
      setErrorMessage(message);
      notifyAndLogError('Could not add skill', message, error);
    }
  };

  if (entry.alreadyAdded) {
    return (
      <Group gap={6} tabIndex={-1} ref={addedRef}>
        <IconCheck size={16} />
        <Text size="sm" fw={600} c="gray.5">
          Added
        </Text>
      </Group>
    );
  }

  const buttonProps = {
    variant: 'default' as const,
    maw: BUTTON_MAX_WIDTH,
    styles: { label: { whiteSpace: 'normal' as const } },
    py: 'xs',
  };

  if (isPending) {
    return (
      <Button {...buttonProps} loading disabled>
        Adding
      </Button>
    );
  }

  return (
    <Stack gap={4}>
      <Button {...buttonProps} disabled={errorMessage !== null} onClick={handleAdd}>
        {`Add to ${displayFirmName}`}
      </Button>
      {errorMessage && (
        <Text size="xs" c="red.7" maw={BUTTON_MAX_WIDTH} role="alert">
          {errorMessage}
        </Text>
      )}
    </Stack>
  );
}
